import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';

/**
 * VerbChip — submittal lifecycle verbs from contract E06-F03.
 *
 * README §6: "Each verb has a specific tone (green/amber/red/grey); don't
 * substitute." The tone mapping below is a verbatim port of
 * `design_handoff_caliber_v2/prototype/ui.js → verbChip()` — see ticket
 * T-0094's `Definition of done` section ("Verb chip has correct tone mapping
 * per ui.js → verbChip()").
 *
 * Verb-to-chip-tone (from ui.js):
 *   Approved              → chip-healthy   (posture-healthy tint + ink)
 *   Approved as Noted     → chip-at-risk   (posture-at-risk tint + ink)
 *   Revise & Resubmit     → chip-at-risk
 *   Rejected              → chip-critical  (posture-critical tint + ink)
 *   Outstanding           → chip-unassessed (ink-100 + posture-unassessed)
 *   Submitted             → chip-unassessed
 *   Overdue               → chip-critical
 *
 * The chip itself follows the same pill geometry as the posture chip
 * (22px tall, rounded-full, 12px/500 text, 10px horizontal padding) — these
 * numbers come from prototype `.chip` (tokens.css L142–L148) and are
 * load-bearing across the product: substituting a different chip shape here
 * would visually de-sync the submittals tracker from every other chip on
 * the page.
 *
 * The component accepts the canonical lowercase-snake verb enum (matching
 * how the submittals service represents lifecycle state on the wire). The
 * human-readable label is derived from the enum so callers don't need to
 * pre-format.
 */
export type SubmittalVerb =
  | 'approved'
  | 'approved_as_noted'
  | 'revise'
  | 'rejected'
  | 'outstanding'
  | 'overdue'
  | 'submitted';

const VERB_LABEL: Record<SubmittalVerb, string> = {
  approved: 'Approved',
  approved_as_noted: 'Approved as Noted',
  revise: 'Revise & Resubmit',
  rejected: 'Rejected',
  outstanding: 'Outstanding',
  overdue: 'Overdue',
  submitted: 'Submitted',
};

/**
 * Chip-pill base. Mirrors `.chip` in tokens.css: 22px tall, 10px horizontal
 * padding, 12px/500 text, fully-rounded, white-space:nowrap. Variant classes
 * layer the posture-token bg/fg.
 *
 * Tones use Tailwind utilities backed by the brand tokens (see
 * tailwind.config.ts → colors.posture). The translation between the
 * prototype `.chip-healthy` etc. classes and the Tailwind utilities is
 * 1-to-1; we don't go through the CSS class names because keeping the
 * styling co-located with the component is what makes the primitive
 * portable (e.g., to a Storybook story or a snapshot test).
 */
const verbChipVariants = cva(
  'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-[12px] font-medium leading-[18px]',
  {
    variants: {
      tone: {
        healthy: 'bg-posture-healthy-tint text-posture-healthy',
        'at-risk': 'bg-posture-at-risk-tint text-posture-at-risk',
        critical: 'bg-posture-critical-tint text-posture-critical',
        unassessed: 'bg-ink-100 text-posture-unassessed',
      },
    },
  },
);

const VERB_TONE: Record<SubmittalVerb, VariantProps<typeof verbChipVariants>['tone']> = {
  approved: 'healthy',
  approved_as_noted: 'at-risk',
  revise: 'at-risk',
  rejected: 'critical',
  outstanding: 'unassessed',
  overdue: 'critical',
  submitted: 'unassessed',
};

export interface VerbChipProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  verb: SubmittalVerb;
}

const VerbChip = React.forwardRef<HTMLSpanElement, VerbChipProps>(
  ({ verb, className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(verbChipVariants({ tone: VERB_TONE[verb] }), className)}
      {...props}
    >
      {VERB_LABEL[verb]}
    </span>
  ),
);
VerbChip.displayName = 'VerbChip';

export { VerbChip, VERB_LABEL, VERB_TONE };
